import React from 'react';
import { Box, InputLabel, TextField, InputAdornment, Typography, IconButton, Stack, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { useTheme } from '@mui/material/styles';
import {RestartAlt} from "@mui/icons-material";

const MhdIntegerInput = ({
  label,
  value,
  override,
  warning,
  error,
  onChange,
  disabled,
  units,
  step = '1',
  clear = false,
  min=1,
  max=64
}) => {
  const theme = useTheme();

  // Increment handler: parse current value (or override if provided), add step and clamp.
  const handleIncrement = () => {
    let newValue = parseInt(override ?? value, 10);
    if (isNaN(newValue)) {
      newValue = 0;
    }
    newValue += parseInt(step, 10);
    if (min !== undefined && newValue < min) newValue = min;
    if (max !== undefined && newValue > max) newValue = max;
    onChange(String(newValue));
  };

  // Decrement handler: subtract step and clamp.
  const handleDecrement = () => {
    let newValue = parseInt(override ?? value, 10);
    if (isNaN(newValue)) {
      newValue = 0;
    }
    newValue -= parseInt(step, 10);
    if (min !== undefined && newValue < min) newValue = min;
    if (max !== undefined && newValue > max) newValue = max;
    onChange(String(newValue));
  };

  // Change handler for direct input.
  const handleChange = (e) => {
    let val = e.target.value;
    // If empty string, allow it.
    if (val === '') {
      onChange(val);
      return;
    }
    let intVal = parseInt(val, 10);
    if (isNaN(intVal)) {
      intVal = 0;
    }
    if (min !== undefined && intVal < min) intVal = min;
    if (max !== undefined && intVal > max) intVal = max;
    onChange(String(intVal));
  };

  return (
    <>
      <InputLabel variant={error ? 'error' : 'primary'}>
        {label}
      </InputLabel>
      <Tooltip
        title={warning}
        arrow
        componentsProps={{
          tooltip: { sx: { backgroundColor: theme.palette.error.main, color: 'white' } },
          arrow: { sx: { color: theme.palette.error.main } },
        }}
      >
        <Box sx={{ position: 'relative', display: 'inline-block', width: '100%' }}>
          <TextField
            type="text"
            fullWidth
            value={value ?? ''}
            onChange={handleChange}
            error={error}
            disabled={disabled}
            InputProps={{
              inputProps: {
                step: parseInt(step, 10),
                min: min,
                max: max,
              },
              endAdornment: (
                <InputAdornment position='end' sx={{marginRight: -1}}>
                  {units && <Typography pr='4px'>{units}</Typography>}
                  <Stack direction='column' spacing='3px'>
                    <IconButton
                      onClick={handleIncrement}
                      disabled={disabled}
                      sx={{
                        padding: '5px',
                        height: 4,
                        width: 4,
                        backgroundColor: theme.palette.base.rothko5,
                        color: '#000000',
                        '&:hover': {
                          backgroundColor: theme.palette.base.rothko6,
                        },
                      }}
                    >
                      <AddIcon sx={{fontSize: '5px',}}/>
                    </IconButton>
                    <IconButton
                      onClick={handleDecrement}
                      disabled={disabled}
                      sx={{
                        padding: '5px',
                        height: 4,
                        width: 4,
                        backgroundColor: theme.palette.base.rothko5,
                        color: '#000000',
                        '&:hover': {
                          backgroundColor: theme.palette.base.rothko6,
                        },
                      }}
                    >
                      <RemoveIcon sx={{fontSize: '6px'}}/>
                    </IconButton>
                </Stack>
                </InputAdornment>
              ),
            }}
          />
          <IconButton
            onClick={() => onChange('')}
            color="primary"
            sx={{
              position: 'absolute',
              zIndex: 1,
              right: -8,
              top: -8,
              width: 8,
              height: 8,
            }}
          >
            <RestartAlt fontSize="small" />
          </IconButton>
        </Box>
      </Tooltip>
      {(override || override === 0) && (
        <Typography variant="caption" color="error">
          Modified: {parseInt(override, 10).toExponential(2)} {units}
        </Typography>
      )}
    </>
  );
};

export default MhdIntegerInput;
